import type { ModelInfo } from '@paradigm/terminal-core';

/**
 * Group picker models by provider, derived from the Bedrock inference-profile
 * id — there is no provider field on the wire, but ids follow
 * `[<region>.]<provider>.<model>` (`jp.anthropic.claude-opus-4-8`,
 * `deepseek.v3.2`). The region strip mirrors the sidecar's
 * `stripRegionPrefix` so both ends parse ids the same way.
 *
 * Anthropic always sorts first (it's the default/fallback tier everywhere we
 * deploy); other providers keep first-appearance order so the host's list
 * order still matters. Within a group, model order is preserved.
 */

const REGION_PREFIXES = ['eu.', 'us.', 'apac.', 'jp.', 'global.'] as const;

// A Map, not an object literal: the provider token comes from an untrusted
// id, and object-literal lookup walks the prototype chain (a token like
// "constructor" would return a function instead of falling through).
const PROVIDER_LABELS: ReadonlyMap<string, string> = new Map([
  ['anthropic', 'Anthropic'],
  ['amazon', 'Amazon'],
  ['deepseek', 'DeepSeek'],
  ['google', 'Google'],
  ['meta', 'Meta'],
  ['mistral', 'Mistral'],
  ['openai', 'OpenAI'],
  ['qwen', 'Qwen'],
  ['zai', 'Z.AI'],
]);

const OTHER_LABEL = 'Other';

export function getProviderLabel(id: string): string {
  let rest = id;
  for (const p of REGION_PREFIXES) {
    if (rest.startsWith(p)) {
      rest = rest.slice(p.length);
      break;
    }
  }
  const dot = rest.indexOf('.');
  if (dot <= 0) return OTHER_LABEL;
  const provider = rest.slice(0, dot).toLowerCase();
  const known = PROVIDER_LABELS.get(provider);
  if (known != null) return known;
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

export interface ModelProviderGroup {
  readonly label: string;
  readonly models: readonly ModelInfo[];
}

export function groupModelsByProvider(
  models: readonly ModelInfo[],
): readonly ModelProviderGroup[] {
  const byLabel = new Map<string, ModelInfo[]>();
  for (const m of models) {
    const label = getProviderLabel(m.id);
    const bucket = byLabel.get(label);
    if (bucket == null) byLabel.set(label, [m]);
    else bucket.push(m);
  }
  const labels = [...byLabel.keys()].sort((a, b) => {
    if (a === b) return 0;
    if (a === 'Anthropic') return -1;
    if (b === 'Anthropic') return 1;
    if (a === OTHER_LABEL) return 1;
    if (b === OTHER_LABEL) return -1;
    return 0; // keep first-appearance order (Map iteration is insertion-ordered)
  });
  return labels.map((label) => ({
    label,
    models: byLabel.get(label) ?? [],
  }));
}
